import LoadingQuestion from 'components/LoadingComponents/LoadingQuestion';
import {Button} from 'letrus-ui';
import {useEffect, useState} from 'react';
import {EnrichedQuestion} from 'utils/types/questionnaires';
import {QuestionSubmitParams} from 'containers/PracticeTest';
import styles from './CheckboxQuestion.module.scss';

export interface CheckboxQuestionProps {
  question: EnrichedQuestion;
  onGoBack(): void;
  goToNextQuestion(): void;
  onQuestionSubmit(params: QuestionSubmitParams): void;
  isLoading: boolean;
  isQuestionnaireFinished: boolean;
}

function CheckboxQuestion({
  question,
  onGoBack,
  onQuestionSubmit,
  goToNextQuestion,
  isLoading,
  isQuestionnaireFinished,
}: CheckboxQuestionProps): JSX.Element {
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  useEffect(() => {
    const previousSelections = question.responses?.selectedChoices ?? [];
    setSelectedItems(previousSelections);
  }, [question]);

  function toggleItem(choiceId: number) {
    if (isQuestionnaireFinished) return;

    setSelectedItems((prev) =>
      prev.includes(choiceId)
        ? prev.filter((id) => id !== choiceId)
        : [...prev, choiceId],
    );
  }

  function handleSubmit() {
    onQuestionSubmit({
      questionId: question.id,
      responses: selectedItems.map((option) => ({option})),
    });

    goToNextQuestion();
  }

  return isLoading ? (
    <LoadingQuestion questionType="checkbox" />
  ) : (
    <article className={styles.wrapper}>
      <div className={styles.header}>
        <h1>{question.name}</h1>
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{__html: question.text || ''}} />
      </div>

      <fieldset className={styles.options}>
        <legend className={styles.srOnly}>Opções</legend>
        {question.choices?.map((choice) => (
          <label
            key={choice.id}
            className={`${styles.choice} ${
              selectedItems.includes(choice.id) ? styles.selected : ''
            } ${isQuestionnaireFinished ? styles.disabled : ''}`}
          >
            <input
              type="checkbox"
              value={choice.id}
              checked={selectedItems.includes(choice.id)}
              disabled={isQuestionnaireFinished}
              onChange={() => toggleItem(choice.id)}
              data-testid={`checkbox-${choice.id}`}
            />
            {choice.name}
          </label>
        ))}
      </fieldset>

      <div className={styles.controls}>
        <Button
          type="button"
          variant="secondary"
          onClick={onGoBack}
          disabled={question.order === 1}
        >
          Voltar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={selectedItems.length === 0}
          data-testid="submit-button"
        >
          {question.button_text || 'Próxima'}
        </Button>
      </div>
    </article>
  );
}

export default CheckboxQuestion;
